import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaFlask,
  FaCheckCircle,
  FaTimesCircle,
  FaInbox,
  FaBolt,
} from 'react-icons/fa';
import BroadcastDrawer from './Drawer';
import List from './List';
import BroadcastHeader from './Header';
import WarmupPlanDetails from './WarmupPlanDetails';
import Graph from './Graph';
import MessageDrawer from './MessageDrawer';

export default function BroadcastsPage() {
  const { playlistId, broadcastId } = useParams();
  const navigate = useNavigate();

  const [broadcasts, setBroadcasts] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [broadcastToEdit, setBroadcastToEdit] = useState(null);

  const [testEmails, setTestEmails] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [sending, setSending] = useState(false);

  const [currentBroadcastName, setCurrentBroadcastName] = useState('');
  const [currentBroadcastCurrentDay, setCurrentBroadcastCurrentDay] =
    useState(1);

  const [loadingCardId, setLoadingCardId] = useState(null);
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('prae.jwt');

  const fetchBroadcasts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/broadcasts/playlists/${playlistId}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) throw new Error(`Erreur HTTP : ${response.status}`);
      const data = await response.json();
      setBroadcasts(data);
    } catch (err) {
      // Optionnel : reporter l'erreur dans une bannière/toast
    } finally {
      setLoading(false);
    }
  }, [playlistId, token]);

  useEffect(() => {
    if (playlistId) fetchBroadcasts();
  }, [playlistId, fetchBroadcasts]);

  useEffect(() => {
    if (broadcasts.length > 0 && !broadcastId) {
      navigate(
        `/broadcasts/playlists/${playlistId}/broadcasts/${broadcasts[0].id}`,
        { replace: true }
      );
    }
    if (broadcasts.length > 0 && broadcastId) {
      const current = broadcasts.find(
        b => String(b.id) === String(broadcastId)
      );
      if (current) {
        setCurrentBroadcastName(current.name);
        setCurrentBroadcastCurrentDay(current.current_day || 1);
      }
    }
  }, [broadcasts, broadcastId, playlistId, navigate]);

  const handleCreateBroadcast = () => {
    setBroadcastToEdit(null);
    setDrawerOpen(true);
  };
  const handleEditBroadcast = broadcast => {
    setBroadcastToEdit(broadcast);
    setDrawerOpen(true);
  };
  const handleDrawerSave = async () => {
    await fetchBroadcasts();
    setDrawerOpen(false);
    setBroadcastToEdit(null);
  };
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setBroadcastToEdit(null);
  };
  const handleSelectBroadcast = id => {
    navigate(`/broadcasts/playlists/${playlistId}/broadcasts/${id}`);
  };

  const handleScheduleChange = async (bid, dateStr) => {
    setLoadingCardId(bid);
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/broadcasts/${bid}/schedule`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ scheduled_date: dateStr }),
        }
      );
      await fetchBroadcasts();
    } catch (err) {
      console.error('Erreur lors de la mise à jour du planning', err);
    }
    setLoadingCardId(null);
  };

  const handleDeleteBroadcast = async bid => {
    if (!window.confirm('Confirmer la suppression ?')) return;
    setLoadingCardId(bid);
    await fetch(`${process.env.REACT_APP_API_URL}/broadcasts/${bid}`, {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'multipart/form-data',
      },
    });
    await fetchBroadcasts();
    setLoadingCardId(null);
  };

  const handlePauseBroadcast = async bid => {
    if (!window.confirm('Voulez-vous vraiment mettre en pause ce broadcast ?'))
      return;
    setLoadingCardId(bid);
    await fetch(`${process.env.REACT_APP_API_URL}/broadcasts/${bid}/pause`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    await fetchBroadcasts();
    setLoadingCardId(null);
  };

  const handleResumeBroadcast = async bid => {
    setLoadingCardId(bid);
    await fetch(`${process.env.REACT_APP_API_URL}/broadcasts/${bid}/resume`, {
      method: 'POST',
    });
    await fetchBroadcasts();
    setLoadingCardId(null);
  };

  const handleSendTests = async () => {
    setSending(true);
    setTestResult(null);
    const emails = testEmails
      .split(';')
      .map(e => e.trim())
      .filter(Boolean);

    const successes = [];
    const failures = [];

    await Promise.all(
      emails.map(async email => {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/broadcasts/queue/test-jobs`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ email, broadcastId, day: 0, slot: 0 }),
            }
          );
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Erreur serveur');
          successes.push(data.jobId);
        } catch (err) {
          failures.push({ email, error: err.message });
        }
      })
    );

    setTestResult({ successes, failures });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
      {/* Top header / breadcrumb */}
      <div className="sticky top-0 z-30 backdrop-blur bg-white/70 dark:bg-slate-900/70 border-b border-slate-200/60 dark:border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
              <FaInbox size={14} />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">
                Diffusion
              </div>
              <div className="font-semibold text-slate-900 dark:text-white">
                Broadcasts de la playlist #{playlistId}
              </div>
            </div>
          </div>

          <button
            onClick={handleCreateBroadcast}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm shadow-sm"
          >
            <FaPlus className="opacity-90" />
            Nouveau broadcast
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-12 gap-6">
        {/* Sidebar list */}
        <aside className="col-span-12 lg:col-span-4">
          <div className="sticky top-20">
            <div className="rounded-2xl border border-slate-200 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 flex items-center justify-between">
                <div className="font-semibold text-slate-900 dark:text-white">
                  Broadcasts
                </div>
                <span className="text-xs text-slate-500">
                  {broadcasts?.length || 0}
                </span>
              </div>
              <div className="p-3">
                <List
                  broadcasts={broadcasts}
                  selectedBroadcastId={broadcastId}
                  onSelectBroadcast={handleSelectBroadcast}
                  onEditBroadcast={handleEditBroadcast}
                  onScheduleChange={(id, dateStr) =>
                    handleScheduleChange(id, dateStr)
                  }
                  onDelete={handleDeleteBroadcast}
                  onPause={handlePauseBroadcast}
                  onResume={handleResumeBroadcast}
                  loadingCardId={loadingCardId}
                />
                {loading && (
                  <div className="mt-3 text-center text-sm text-slate-400">
                    Chargement…
                  </div>
                )}
                {!loading && broadcasts?.length === 0 && (
                  <div className="mt-2 text-center text-slate-500 text-sm">
                    Aucun broadcast pour cette playlist.
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="col-span-12 lg:col-span-8 space-y-6">
          {/* Header (actions liés au message) */}
          <div className="rounded-2xl border border-slate-200 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="p-5">
              <BroadcastHeader
                broadcastName={currentBroadcastName}
                onOpenMessageDrawer={() => setIsMessageDrawerOpen(true)}
                setShowAI={() => {
                  /* placeholder for AI panel */
                }}
                playlistId={playlistId}
                broadcastId={broadcastId}
              />
            </div>
          </div>

          {/* Test send card */}
          <section className="rounded-2xl border border-slate-200 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white">
                <FaFlask size={14} />
              </span>
              <div className="font-semibold text-slate-900 dark:text-white">
                Envoyer des tests
              </div>
            </div>
            <div className="p-5">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Destinataires (séparés par <code>;</code>)
              </label>
              <textarea
                rows={2}
                className="w-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:focus:ring-emerald-900/50"
                placeholder="test1@example.com; test2@example.com"
                value={testEmails}
                onChange={e => setTestEmails(e.target.value)}
                disabled={sending}
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={handleSendTests}
                  disabled={sending || !testEmails.trim()}
                  className="px-4 py-2 rounded-xl bg-emerald-600 enabled:hover:bg-emerald-700 disabled:opacity-50 text-white text-sm shadow-sm"
                >
                  {sending ? 'Envoi en cours…' : 'Envoyer les tests'}
                </button>
                {!!testEmails && (
                  <button
                    type="button"
                    onClick={() => setTestEmails('')}
                    className="px-3 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm"
                  >
                    Effacer
                  </button>
                )}
              </div>

              {testResult && (
                <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold mb-2">
                      <FaCheckCircle /> Succès ({testResult.successes.length})
                    </div>
                    {testResult.successes.length === 0 ? (
                      <div className="text-sm text-slate-500">
                        Aucun job en file.
                      </div>
                    ) : (
                      <div className="text-xs text-slate-600 break-words">
                        {testResult.successes.join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-semibold mb-2">
                      <FaTimesCircle /> Erreurs ({testResult.failures.length})
                    </div>
                    {testResult.failures.length === 0 ? (
                      <div className="text-sm text-slate-500">
                        Aucune erreur.
                      </div>
                    ) : (
                      <ul className="list-disc list-inside text-sm text-slate-700 dark:text-slate-300">
                        {testResult.failures.map(f => (
                          <li key={f.email}>
                            <span className="font-medium">{f.email}</span> :{' '}
                            {f.error}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Graph */}
          <section className="rounded-2xl border border-slate-200 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
                <FaBolt size={14} />
              </span>
              <div className="font-semibold text-slate-900 dark:text-white">
                Plan de warmup (top domaines)
              </div>
            </div>
            <div className="p-5">
              <Graph />
            </div>
          </section>

          {/* Warmup details */}
          <section className="rounded-2xl border border-slate-200 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 font-semibold text-slate-900 dark:text-white">
              Détail du warmup par jour
            </div>
            <div className="p-5">
              <WarmupPlanDetails
                broadcastId={broadcastId}
                currentDay={currentBroadcastCurrentDay}
              />
            </div>
          </section>
        </main>
      </div>

      {/* Drawers */}
      <BroadcastDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        playlistId={playlistId}
        broadcastToEdit={broadcastToEdit}
        onSave={handleDrawerSave}
      />

      <MessageDrawer
        playlistId={playlistId}
        isOpen={isMessageDrawerOpen}
        onClose={() => setIsMessageDrawerOpen(false)}
      />

      {/* Floating action button (mobile) */}
      <button
        onClick={handleCreateBroadcast}
        className="lg:hidden fixed bottom-6 right-6 h-12 w-12 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg flex items-center justify-center"
        aria-label="Créer un broadcast"
      >
        <FaPlus />
      </button>
    </div>
  );
}
